import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Truck, UserCheck } from 'lucide-react'
import { MultiSelect } from '@/components/common/MultiSelect'
import CommonModal from '@/components/common/CommonModal'
import Button from '@/components/common/Button'
import { callApi, ThrowError } from '@/api/callApi'
import { API_ENDPOINT, API_URL } from '@/api/constant'
import { IDrivers, IAvailableDriversResponse, DriverStatus } from '@/interfaces/Driver/driver.interface'
import { GetFilterUrl, showToastNotification } from '@/utility/helper'
import { RootState, useAppSelector } from '@/redux/store'
import { IReturnOrder } from '@/interfaces/ReturnOrders/returnOrder.interface'
import { IPageInfo } from '@/interfaces/common.interface'

interface AssignDriverModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    orderId: string
    onSuccess: () => void
    isReturn?: boolean
    returnRequest?: IReturnOrder | null
    currentDriverId?: string
    currentDriver?: IDrivers | any
}

export const AssignDriverModal: React.FC<AssignDriverModalProps> = ({
    open,
    onOpenChange,
    orderId,
    onSuccess,
    isReturn,
    returnRequest,
    currentDriverId,
    currentDriver,
}) => {
    if (!open) return null;

    const [drivers, setDrivers] = useState<IDrivers[]>([]);
    const [selectedDriverId, setSelectedDriverId] = useState<string[]>([]);
    const [isFetchingDrivers, setIsFetchingDrivers] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDriverExist, setIsDriverExist] = useState(false);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Fetch drivers with optional search term and limit=100 ────────────────
    const fetchDrivers = useCallback(async (query = '') => {
        setIsFetchingDrivers(true);
        try {
            const queryParams = GetFilterUrl({
                search: query,
                limit: 100
            });

            const response = await callApi<IAvailableDriversResponse>(
                `${API_URL}${API_ENDPOINT.drivers}/available?${queryParams.toString()}`,
                { method: 'GET' }
            );

            // Extract drivers safely from both legacy array and new paginated response formats
            let incoming: IDrivers[] = [];
            if (Array.isArray(response?.users)) {
                incoming = response.users;
            } else if (Array.isArray(response?.data)) {
                incoming = response.data;
            } else if (response?.data && typeof response.data === 'object' && 'users' in response.data) {
                incoming = Array.isArray(response.data.users) ? response.data.users : [];
            }

            const initialId = currentDriverId || currentDriver?.id;
            const findDriver = incoming.find(d => d.id === initialId) || (currentDriver?.id === initialId ? currentDriver : null);

            let merged = [...incoming];
            if (findDriver && !merged.some(d => d.id === findDriver.id)) {
                merged.unshift(findDriver);
            }

            setDrivers(merged);

            if (findDriver && initialId) {
                setIsDriverExist(true);
                setSelectedDriverId([initialId]);
            } else {
                setIsDriverExist(false);
            }
        } catch (error) {
            console.error('Failed to fetch drivers', error);
        } finally {
            setIsFetchingDrivers(false);
        }
    }, [currentDriverId, currentDriver]);

    // ── Reset & initial load when modal opens ──────────────────────────────
    useEffect(() => {
        setIsDriverExist(false);
        setSelectedDriverId([]);
        setSearchQuery('');
        setDrivers([]);
        fetchDrivers('');
    }, [fetchDrivers]);

    // ── Debounced search handler ────────────────────────────────────────────
    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            fetchDrivers(value);
        }, 350);
    };

    // ── Assign driver ───────────────────────────────────────────────────────
    const handleAssignDriver = async () => {
        if (!selectedDriverId.length) {
            showToastNotification('danger', 'Please select a driver first');
            return;
        }

        setAssignLoading(true);
        try {
            if (isReturn) {
                // 1. Register the driver assignment in the core ride registry

                // 2. Update the return request record with the driver and pickup_assigned status
                const returnPayload = {
                    pickupDriverId: selectedDriverId[0],
                    status: 'pickup_assigned',
                };
                await callApi(
                    `${API_URL}${API_ENDPOINT.return}/${returnRequest?.id}`,
                    { method: 'PATCH', data: returnPayload }
                );
            } else {
                const payload = {
                    orderIds: [orderId],
                    driverId: selectedDriverId[0],
                    status: 'assigned',
                };
                await callApi(
                    `${API_URL}${API_ENDPOINT.assignDriver}`,
                    { method: 'PATCH', data: payload }
                );
            }

            showToastNotification('success', 'Driver assigned successfully');
            onOpenChange(false);
            onSuccess();
        } catch (error) {
            ThrowError(error);
        } finally {
            setAssignLoading(false);
        }
    };

    // Selected driver details (for the preview pill)
    const selectedDriver = drivers.find(d => d.id === selectedDriverId[0]);

    return (
        <CommonModal
            open={open}
            onOpenChange={onOpenChange}
            title="Assign Driver"
            size="md"
        >
            <div className="space-y-6">

                {/* ── Driver Search & Select ── */}
                <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Truck className="w-4 h-4 text-primary" />
                        Search & Select Driver
                    </label>

                    <MultiSelect
                        options={drivers.map(d => {
                            const status = d.driverInfo?.driverStatus;
                            let statusText = '[Available]';
                            let statusColor = '#10b981'; // emerald-500
                            let disabled = false;

                            if (status === DriverStatus.ON_RIDE) {
                                statusText = '(On Ride - Please wait)';
                                statusColor = '#f59e0b'; // amber-500
                                disabled = false;
                            } else if (status === DriverStatus.ON_DELIVERY) {
                                statusText = '(On Delivery - Please wait)';
                                statusColor = '#3b82f6'; // blue-500
                                disabled = true;
                            } else if (status === DriverStatus.AVAILABLE) {
                                statusText = '[Available]';
                                statusColor = '#10b981'; // emerald-500
                                disabled = false;
                            }

                            const fullText = `${d.fullName} — ${d.countryCode ?? ''}${d.phone} ${statusText}`;

                            return {
                                label: fullText,
                                value: d.id,
                                disabled: disabled,
                                color: statusColor,
                                title: fullText
                            };
                        })}
                        selected={selectedDriverId}
                        onChange={setSelectedDriverId}
                        onSearchChange={handleSearchChange}
                        placeholder={isFetchingDrivers ? 'Searching drivers...' : 'Type a name or phone to search...'}
                        singleSelect
                        loading={isFetchingDrivers}
                    />

                    {/* Empty state */}
                    {drivers.length === 0 && !isFetchingDrivers && (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-xl mt-1 border border-amber-100 italic">
                            {searchQuery
                                ? `No drivers found for "${searchQuery}". Try a different name or phone number.`
                                : 'No verified drivers found in the system.'}
                        </p>
                    )}
                </div>

                {/* ── Selected Driver Preview ── */}
                {selectedDriver && (
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-primary/5 border border-primary/15">
                        <div className="p-2 bg-primary/10 rounded-xl shrink-0">
                            <UserCheck className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground truncate">
                                {selectedDriver.fullName}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                                {selectedDriver.countryCode}{selectedDriver.phone}
                            </p>
                        </div>
                        <span className="ml-auto shrink-0 text-[10px] font-bold px-2.5 py-1 bg-green-100 text-green-700 rounded-full">
                            Selected
                        </span>
                    </div>
                )}

                {/* ── Actions ── */}
                <div className="flex gap-3 pt-4 border-t border-dashed">
                    <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => onOpenChange(false)}
                        disabled={assignLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="flex-1"
                        onClick={handleAssignDriver}
                        isLoading={assignLoading}
                        disabled={selectedDriverId.length === 0}
                    >
                        Assign Driver
                    </Button>
                </div>
            </div>
        </CommonModal>
    )
}
